import { styled, XStack } from "tamagui";
import { AlertCircle, CheckCircle2 } from "@tamagui/lucide-icons";
import { Icon } from "../Icon";
import { Body3, Caption } from "../Typography";
import type { FieldValidation } from "./FieldShell";
const InfoRowContainer = styled(XStack, {
  name: "InfoRowContainer",
  paddingTop: "$sp0.5",
  paddingHorizontal: "$sp2",
  alignItems: "center",
  justifyContent: "space-between"
});
const HelperTextContainer = styled(XStack, {
  name: "HelperTextContainer",
  alignItems: "center",
  gap: 6,
  flex: 1
});
const HelperText = styled(Body3, {
  name: "HelperText",
  color: "$textSubtle",
  variants: {
    validation: {
      none: {
        color: "$textSubtle"
      },
      error: {
        color: "$textNegative"
      },
      success: {
        color: "$textPositive"
      }
    }
  } as const,
  defaultVariants: {
    validation: "none"
  }
});
const CharacterCount = styled(Caption, {
  name: "CharacterCount",
  color: "$textSubtle",
  textAlign: "right"
});
const HELPER_ICON_SIZE = 12;
const HelperIcon = ({
  validation
}: {
  validation: FieldValidation;
}) => {
  if (validation === "none") {
    return null;
  }
  if (validation === "error") {
    return <Icon icon={AlertCircle} size={HELPER_ICON_SIZE} color="$iconNegative" />;
  }
  return <Icon icon={CheckCircle2} size={HELPER_ICON_SIZE} color="$iconPositive" data-sentry-element="Icon" data-sentry-component="HelperIcon" data-sentry-source-file="FieldHelperRow.tsx" />;
};
export type FieldHelperRowProps = {
  validation?: FieldValidation;
  helperText?: string;
  helperTextId?: string;
  showCharacterCount?: boolean;
  currentLength?: number;
  maxLength?: number;
};

/**
 * The helper / validation / character-count row beneath a field frame. Extracted
 * from `FieldShell` so a composite field can position it independently — e.g. a
 * multi-select, whose value overflows into chips below the frame, renders this
 * *below the chips* rather than letting FieldShell wedge it between the frame and
 * the chips. The trigger↔helper link stays id-based via `helperTextId`, so it
 * works wherever the row renders.
 */
export const FieldHelperRow = ({
  validation = "none",
  helperText,
  helperTextId,
  showCharacterCount = false,
  currentLength = 0,
  maxLength
}: FieldHelperRowProps) => {
  const showCharCount = showCharacterCount && maxLength !== undefined;
  if (!helperText && !showCharCount) {
    return null;
  }
  const isInvalid = validation === "error";
  return <InfoRowContainer data-sentry-element="InfoRowContainer" data-sentry-component="FieldHelperRow" data-sentry-source-file="FieldHelperRow.tsx">
      <HelperTextContainer id={helperTextId} role={isInvalid ? "alert" : undefined} aria-live={isInvalid ? "polite" : "off"} data-sentry-element="HelperTextContainer" data-sentry-source-file="FieldHelperRow.tsx">
        <HelperIcon validation={validation} data-sentry-element="HelperIcon" data-sentry-source-file="FieldHelperRow.tsx" />
        {helperText ? <HelperText validation={validation}>{helperText}</HelperText> : null}
      </HelperTextContainer>
      {showCharCount ? <CharacterCount>
          {currentLength}/{maxLength}
        </CharacterCount> : null}
    </InfoRowContainer>;
};